import { useState } from "react";
import { Brain, Gamepad2, Newspaper, LineChart, Bot, Check, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { scrollTo } from "./Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = [
  {
    id: "academy",
    icon: Brain,
    name: "Academy",
    tagline: "Premium live coaching and mentorship, built around psychology and discipline.",
    live: true,
  },
  {
    id: "trading-trainer",
    icon: Gamepad2,
    name: "Trading Trainer",
    tagline: "Gamified, step by step learning that takes beginners from zero to confident.",
    live: true,
    cta: "Get Trading Trainer",
  },
  {
    id: "market-intelligence",
    icon: Newspaper,
    name: "MBM Intelligence",
    tagline: "Daily AI market briefs that explain what is moving, in plain language.",
    live: true,
    href: "/intelligence",
    cta: "Open Intelligence",
  },
  {
    id: "indicators-tools",
    icon: LineChart,
    name: "MBM Indicators",
    tagline: "A proprietary TradingView suite for analysis and decision support.",
    live: false,
  },
  {
    id: "ai-pro",
    icon: Bot,
    name: "AI Pro",
    tagline: "A standalone AI trading mentor, trained on the MBM way.",
    live: false,
  },
];

function WaitlistForm({ product }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product, email: email.trim() }),
      });
      if (!r.ok) throw new Error();
      setDone(true);
    } catch {
      setError("Couldn't join right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="mt-5 flex items-center gap-2 text-[#7ee0a1] text-sm font-light" data-testid={`waitlist-done-${product}`}>
        <Check className="h-4 w-4" strokeWidth={1.75} /> You're on the list.
      </div>
    );

  return (
    <form onSubmit={submit} className="mt-5" data-testid={`waitlist-form-${product}`}>
      <div className="flex items-center gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          className="flex-1 bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-3 py-2.5 text-sm outline-none transition-colors placeholder:text-zinc-600"
          data-testid={`waitlist-email-${product}`}
        />
        <button
          type="submit"
          disabled={busy}
          className="btn-glass text-white px-4 py-2.5 text-xs font-medium tracking-wide whitespace-nowrap disabled:opacity-50"
          data-testid={`waitlist-submit-${product}`}
        >
          {busy ? "…" : "Join Waitlist"}
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red-400 font-light">{error}</div>}
    </form>
  );
}

function ProductCard({ p, i }) {
  const Icon = p.icon;
  return (
    <Reveal delay={i * 0.08}>
      <div
        className={`glass-card h-full flex flex-col p-8 ${p.live ? "glass-card-featured" : ""}`}
        data-testid={`ecosystem-card-${p.id}`}
      >
        <div className="flex items-center justify-between">
          <span className="h-12 w-12 rounded-full flex items-center justify-center border border-white/10">
            <Icon strokeWidth={1.25} className="h-5 w-5 text-[#9fd3ff]" />
          </span>
          <span
            className={`section-label text-[8px] px-2 py-1 ${
              p.live ? "text-[#0A0A0A] bg-[#7ee0a1]" : "text-zinc-400 border border-white/15"
            }`}
          >
            {p.live ? "Live Now" : "Coming Soon"}
          </span>
        </div>

        <h3 className="mt-6 font-heading text-2xl font-light">{p.name}</h3>
        <p className="mt-3 text-sm text-zinc-400 font-light leading-relaxed flex-1">{p.tagline}</p>

        {p.live ? (
          p.href ? (
            <a
              href={p.href}
              className="btn-solid mt-6 py-3 text-sm font-medium tracking-wide inline-flex items-center justify-center gap-2"
              data-testid={`ecosystem-open-${p.id}`}
            >
              {p.cta || "Open"} <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
          ) : (
            <button
              onClick={() => scrollTo("pricing")}
              className="btn-solid mt-6 py-3 text-sm font-medium tracking-wide inline-flex items-center justify-center gap-2"
              data-testid={p.id === "academy" ? "ecosystem-academy-cta" : `ecosystem-cta-${p.id}`}
            >
              {p.cta || "Explore Academy"} <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
          )
        ) : (
          <WaitlistForm product={p.id} />
        )}
      </div>
    </Reveal>
  );
}

export default function Ecosystem() {
  return (
    <section id="ecosystem" className="relative py-24 sm:py-32 lg:py-40 border-t border-white/5" data-testid="ecosystem-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <SectionLabel>The Ecosystem</SectionLabel>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-2xl">
            Five products, one flywheel.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-sm text-zinc-500 font-light max-w-xl">
            Everything under one roof, each part making the others stronger. Academy members get
            up to 50% off every future ecosystem product.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
